import React, { useCallback, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { mergeClasses } from '@fluentui/react-components';
import { CopilotRepositoryProvider, useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { useBrandKitDataService } from '@bingads-webui-campaign-react/brandkit-data-service';
import { formatBrandkitForAPI } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { TemplateGrid, TemplateDetailsModal } from './components';
import { useVideoTemplatesPageStyles } from './style';

const VideoTemplatesPage = ({
  i18n,
  aid,
  logger,
  copilotFinalUrl,
  setCopilotFinalUrl,
  navigation,
  copilotDomainDataService,
  notificationApi,
  pagePerfMarker,
  setDataLoaded,
  loadClipchampPlayer,
}) => {
  const classes = useVideoTemplatesPageStyles();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoadingBrandKit, setIsLoadingBrandkit] = useState(false);
  const [brandkit, setBrandKit] = useState(null);
  const [isBrandKitEmpty, setIsBrandKitEmpty] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const { setDataPromise } = useContext(DataContext);
  const activity = useContext(ActivityContext);

  const repository = useCopilotRepositoryContext();
  const {
    fetchVideoAdsAssetRecommendations,
  } = repository;

  const fetchCount = 10;

  const { getBrandKitDataByAccId } = useBrandKitDataService();

  const fetchBrandKit = useCallback(async () => {
    setIsLoadingBrandkit(true);
    const response = await getBrandKitDataByAccId({ accId: aid });
    setIsLoadingBrandkit(false);
    const isBrandKitHavingData = response && response.BrandKits && response.BrandKits[0] && (!_.isEmpty(response.BrandKits[0].Fonts) ||
    !_.isEmpty(response.BrandKits[0].Images) ||
    !_.isEmpty(response.BrandKits[0].LandscapeLogos) ||
    !_.isEmpty(response.BrandKits[0].Palettes) ||
    !_.isEmpty(response.BrandKits[0].SquareLogos));
    if (isBrandKitHavingData) {
      setIsBrandKitEmpty(false);
      const updatedResponse = {
        ...response.BrandKits[0],
      };
      setBrandKit(formatBrandkitForAPI(updatedResponse));
    } else {
      setIsBrandKitEmpty(true);
    }
  }, [getBrandKitDataByAccId, aid, setBrandKit, setIsBrandKitEmpty]);

  const onFetchVideoRecommendations = useCallback(() => {
    if (isLoadingBrandKit) {
      return null;
    }
    const templateFilter = !_.isEmpty(selectedTemplate) ? { TemplateIds: [selectedTemplate.TemplateId] } : null;
    return fetchVideoAdsAssetRecommendations(fetchCount, false, null, templateFilter, (!isBrandKitEmpty ? brandkit : null));
  }, [brandkit, isLoadingBrandKit, fetchVideoAdsAssetRecommendations, fetchCount, isBrandKitEmpty, selectedTemplate]);


  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  const fetchVideoTemplates = useCallback(async () => {
    pagePerfMarker.willFetchData();
    const { response, error } = await copilotDomainDataService.getVideoTemplates(null, i18n);
    if (error) {
      onFailedToLoadData();
      setIsPageLoading(false);
      if (logger) {
        logger.logTrace('Video Templates - failed to load templates', error);
      }
      return;
    }
    if (activity && activity.signal) {
      activity.signal('[VideoTemplatesPage] Templates Loaded');
    }
    setTemplates((response && response.Templates) || []);
    setIsPageLoading(false);
    pagePerfMarker.willRender();
    if (setDataLoaded) {
      setDataLoaded();
    }
  }, [activity, copilotDomainDataService, onFailedToLoadData, logger, pagePerfMarker, setDataLoaded, i18n]);

  useEffect(() => {
    fetchVideoTemplates();
  }, [fetchVideoTemplates]);

  useEffect(() => {
    fetchBrandKit();
  }, [fetchBrandKit]);

  const onViewTemplateDetails = useCallback((template) => {
    setSelectedTemplate(template);
    if (activity && activity.signal) {
      activity.signal(`[VideoTemplatesPage] templateSelected:${template.TemplateId}`);
    }
    setOpenModal(true);
  }, [activity]);

  const onChooseTemplate = useCallback(() => {
    const dataPromise = onFetchVideoRecommendations();

    const modifiedDataPromise = dataPromise.then(data => ({
      ...data,
      finalUrl: copilotFinalUrl,
    }));

    setDataPromise(modifiedDataPromise);
    setOpenModal(false);
    navigation.gotoCreateVideoPage();
  }, [navigation, setDataPromise, onFetchVideoRecommendations]);

  const onCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  const getAssetCountInfo = (templateItem) => {
    const { NumberOfLogos, NumberOfImages, NumberOfText } = templateItem;
    const result = [];
    if (NumberOfText && NumberOfText > 1) result.push(i18n.getString(_TL_('{{NumberOfText}} texts'), { NumberOfText }));
    else if (NumberOfText && NumberOfText > 0) result.push(i18n.getString(_TL_('{{NumberOfText}} text'), { NumberOfText }));
    if (NumberOfImages && NumberOfImages > 1) result.push(i18n.getString(_TL_('{{NumberOfImages}} images'), { NumberOfImages }));
    else if (NumberOfImages && NumberOfImages > 0) result.push(i18n.getString(_TL_('{{NumberOfImages}} image'), { NumberOfImages }));
    if (NumberOfLogos && NumberOfLogos > 1) result.push(i18n.getString(_TL_('{{NumberOfLogos}} logos'), { NumberOfLogos }));
    else if (NumberOfLogos && NumberOfLogos > 0) result.push(i18n.getString(_TL_('{{NumberOfLogos}} logo'), { NumberOfLogos }));
    return result.join('・');
  };

  const templateTagOptions = {
    all: _TL_('All'),
    performance: _TL_('Performance'),
    brandAwareness: _TL_('Brand awareness'),
    premiumStreaming: _TL_('Premium Streaming'),
    onlineVideo: _TL_('Online Video'),
  };

  const templateTagsMapping = {
    '752ca3e5-d09a-4ca7-bdbc-f9c14b2148aa': {
      tags: [templateTagOptions.brandAwareness, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000001': {
      tags: [templateTagOptions.brandAwareness, templateTagOptions.premiumStreaming],
    },
    '00000002-0000-0000-0000-000000000002': {
      tags: [templateTagOptions.brandAwareness, templateTagOptions.onlineVideo],
    },
    '00000002-0000-0000-0000-000000000003': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000004': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000005': {
      tags: [templateTagOptions.premiumStreaming, templateTagOptions.brandAwareness],
    },
    '00000002-0000-0000-0000-000000000007': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000008': {
      tags: [templateTagOptions.premiumStreaming, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000009': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000010': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.performance],
    },
    '00000002-0000-0000-0000-000000000011': {
      tags: [templateTagOptions.onlineVideo, templateTagOptions.brandAwareness],
    },
  };

  return (
    <div className={mergeClasses('ads-studio-video-template-page', classes.videoTemplatePage)}>
      <TemplateGrid
        loading={isPageLoading}
        templates={templates}
        setClickedTemplate={onViewTemplateDetails}
        templateTagOptions={templateTagOptions}
        i18n={i18n}
        logger={logger}
        getAssetCountInfo={getAssetCountInfo}
        templateTagsMapping={templateTagsMapping}
      />
      {openModal && <TemplateDetailsModal
        template={selectedTemplate}
        show={openModal}
        onShowChange={setOpenModal}
        onSelectTemplate={onChooseTemplate}
        setCopilotFinalUrl={setCopilotFinalUrl}
        onClose={onCloseModal}
        i18n={i18n}
        logger={logger}
        getAssetCountInfo={getAssetCountInfo}
        loadClipchampPlayer={loadClipchampPlayer}
      />}
    </div>
  );
};

VideoTemplatesPage.propTypes = {
  copilotDomainDataService: PropTypes.shape({
    getVideoTemplates: PropTypes.func,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({
    gotoEditVideoPage: PropTypes.func.isRequired,
  }).isRequired,
  setCopilotFinalUrl: PropTypes.func.isRequired,
  setDataLoaded: PropTypes.func.isRequired,
  pagePerfMarker: PropTypes.shape({
    willFetchData: PropTypes.func,
    willRender: PropTypes.func,
  }).isRequired,
  loadClipchampPlayer: PropTypes.func.isRequired,
};

VideoTemplatesPage.defaultProps = {
  notificationApi: {
    showWarning: () => {},
  },
  adsStudioUserPreferences: undefined,
  reload: null,
};

const VideoTemplatesPageWithContexts = ({ ...props }) => {
  const [copilotFinalUrl, setCopilotFinalUrl] = React.useState('');
  const serverContext = useContext(ServerContext);
  const { permissions } = props;
  const useClipchamp = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;

  return (
    <CopilotRepositoryProvider
      campaignId="-1"
      finalUrl={copilotFinalUrl}
      locale={serverContext.CurrentLocale}
      recommendationType="Video"
      serverContext={serverContext}
      useClipchamp={useClipchamp}
    >
      <VideoTemplatesPage {...props} copilotFinalUrl={copilotFinalUrl} setCopilotFinalUrl={setCopilotFinalUrl} />
    </CopilotRepositoryProvider>);
};

VideoTemplatesPageWithContexts.propTypes = {};

const WrappedComponent = VideoTemplatesPageWithContexts;

export { WrappedComponent as VideoTemplatesPageComponent };
